/**
 * 売上・原価の一括取り込み(Gate 収入側「台帳」の型・2026-10-23 段階3)。全体設計 5章。
 * 卸(発注管理表)・F営業(売上管理表)などの台帳から、シートで決まった列に並べ替えて貼り付けて登録する。
 * 台帳そのものの形式は未確認(U8)のため、第1版は「見出しで列を探す」共通の形にする。
 *
 *  - 登録の中身は画面の「売上・原価入力」と同じ(承認フローを通さず計上済み。売上・原価の科目だけ)
 *  - 見出し(順番は問わない): 計上日・事業部・勘定科目・科目②・取引先・金額・摘要・会社。必須は 計上日・事業部・勘定科目・金額
 *  - 事業部・勘定科目・科目②・取引先・会社は、ID・コード・名前のどれでも探す(全角/半角・空白は無視)
 *  - 科目②が空で、その科目に科目②が1つしかなければそれを使う
 *  - 金額は税込・1円以上の整数(カンマ・円・全角数字は読める)
 *  - 既に同じ売上(同じ計上日・事業部・科目・金額・取引先)が登録されていれば「重複」として登録しない(2回貼っても増えない)
 *
 * 具体例は tests/unit/salesImport.test.ts
 */
import type { CsvTable } from '@core/cash/csv';
import { parseAmount } from '@core/lib/money';
import type { Account, Company, DateStr, Department, SubAccount, Transaction, Vendor } from '@core/lib/types';
import { nameKey } from '@core/partners/terms';
import type { DirectEntryInput } from '@core/transactions/revenueRepo';

export const SALES_IMPORT_HEADERS = ['計上日', '事業部', '勘定科目', '科目②', '取引先', '金額', '摘要', '会社'] as const;
type Header = (typeof SALES_IMPORT_HEADERS)[number];

export type SalesImportStatus = 'new' | 'duplicate' | 'error';

export type SalesImportRow = {
  /** 行番号(見出しを1行目とする) */
  line: number;
  status: SalesImportStatus;
  message: string;
  input: DirectEntryInput | null;
};

export type SalesImportMasters = {
  departments: Pick<Department, 'id' | 'code' | 'name' | 'short_name'>[];
  accounts: Pick<Account, 'id' | 'code' | 'name' | 'pl_line' | 'active'>[];
  subAccounts: Pick<SubAccount, 'id' | 'account_id' | 'code' | 'name' | 'active'>[];
  vendors: Pick<Vendor, 'id' | 'code' | 'name'>[];
  companies: Pick<Company, 'id' | 'code' | 'name' | 'is_default'>[];
};

/** 日付の読み取り: 2026-10-31 / 2026/10/31 / 2026/1/5 / 2026年1月5日 */
export function parseDate(raw: string): DateStr | null {
  const s = raw.normalize('NFKC').trim();
  const m = /^(\d{4})[-/年.](\d{1,2})[-/月.](\d{1,2})日?$/.exec(s);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(y, mo - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== mo - 1 || date.getUTCDate() !== d) return null;
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** ID・コード・名前のどれかで1件だけ見つかればそれを返す。0件・2件以上は null */
function findOne<T extends { id: string }>(list: T[], raw: string, keys: (x: T) => Array<string | null | undefined>): T | null | 'many' {
  const k = nameKey(raw);
  if (!k) return null;
  const hits = list.filter((x) => keys(x).some((v) => !!v && nameKey(v) === k));
  if (hits.length === 1) return hits[0]!;
  return hits.length === 0 ? null : 'many';
}

/**
 * 重複を見分けるための鍵(同じ計上日・事業部・科目①②・会社・金額)。取引先は鍵に入れず別に比べる:
 * 取引先が同じ、または登録済みの方に取引先が無い(以前の手入力)なら重複とみなす
 */
export function duplicateKeyOf(
  t: Pick<Transaction, 'posting_date' | 'department_id' | 'account_id' | 'amount'> & { sub_account_id?: string | null; company_id?: string | null },
): string {
  return [t.posting_date, t.department_id, t.account_id, t.sub_account_id ?? '', t.company_id ?? '', t.amount].join('|');
}

export function checkSalesImport(params: {
  table: CsvTable;
  masters: SalesImportMasters;
  /** 取り込む月にすでにある取引(重複の判定に使う。差戻し・取消は除いて渡す) */
  existing: Array<
    Pick<Transaction, 'posting_date' | 'department_id' | 'account_id' | 'amount' | 'vendor_id' | 'type'> &
      Partial<Pick<Transaction, 'sub_account_id' | 'company_id'>>
  >;
}): SalesImportRow[] {
  const { table, masters, existing } = params;
  const col = (name: Header) => table.headers.findIndex((h) => h.normalize('NFKC').trim() === name.normalize('NFKC'));
  const idx = Object.fromEntries(SALES_IMPORT_HEADERS.map((h) => [h, col(h)])) as Record<Header, number>;
  const missing = (['計上日', '事業部', '勘定科目', '金額'] as Header[]).filter((h) => idx[h] < 0);
  if (missing.length > 0) {
    return [{ line: 1, status: 'error', message: `見出しに「${missing.join('」「')}」の列がありません`, input: null }];
  }
  const defaultCompany = masters.companies.find((c) => c.is_default) ?? masters.companies[0];
  // 鍵 → 登録済みの取引先(null = 取引先なし)
  const existingVendors = new Map<string, Array<string | null>>();
  for (const t of existing) {
    if (t.type === 'transfer') continue;
    const k = duplicateKeyOf(t);
    existingVendors.set(k, [...(existingVendors.get(k) ?? []), t.vendor_id ?? null]);
  }
  const seenInFile = new Map<string, number>();

  return table.rows
    .map((cells, i) => ({ cells, line: i + 2 }))
    .filter(({ cells }) => cells.some((c) => c.trim() !== ''))
    .map(({ cells, line }): SalesImportRow => {
      const get = (h: Header) => (idx[h] >= 0 ? (cells[idx[h]] ?? '').trim() : '');
      const errors: string[] = [];

      const postingDate = parseDate(get('計上日'));
      if (!postingDate) errors.push('計上日は 2026-10-31 の形で書いてください');

      const dept = findOne(masters.departments, get('事業部'), (d) => [d.id, d.code, d.name, d.short_name]);
      if (!get('事業部')) errors.push('事業部が空です');
      else if (dept === null) errors.push(`事業部「${get('事業部')}」が見つかりません`);
      else if (dept === 'many') errors.push(`事業部「${get('事業部')}」が複数あります(コードで書いてください)`);

      const account = findOne(
        masters.accounts.filter((a) => a.pl_line === 'revenue' || a.pl_line === 'cogs'),
        get('勘定科目'),
        (a) => [a.id, a.code, a.name],
      );
      if (!get('勘定科目')) errors.push('勘定科目が空です');
      else if (account === null) errors.push(`勘定科目「${get('勘定科目')}」は売上・原価の科目にありません`);
      else if (account === 'many') errors.push(`勘定科目「${get('勘定科目')}」が複数あります(コードで書いてください)`);

      let subAccountId = '';
      if (account && account !== 'many') {
        const subs = masters.subAccounts.filter((s) => s.account_id === account.id && s.active !== false);
        const rawSub = get('科目②');
        if (rawSub) {
          const sub = findOne(subs, rawSub, (s) => [s.id, s.code, s.name]);
          if (sub === null || sub === 'many') errors.push(`科目②「${rawSub}」が「${account.name}」の中に見つかりません`);
          else subAccountId = sub.id;
        } else if (subs.length === 1) {
          subAccountId = subs[0]!.id;
        } else {
          errors.push(`「${account.name}」は科目②を選んでください`);
        }
      }

      let vendorId: string | null = null;
      const rawVendor = get('取引先');
      if (rawVendor) {
        const v = findOne(masters.vendors, rawVendor, (x) => [x.id, x.code, x.name]);
        if (v === null) errors.push(`取引先「${rawVendor}」が取引先マスタにありません(先に取引先を登録してください)`);
        else if (v === 'many') errors.push(`取引先「${rawVendor}」が複数あります(コードで書いてください)`);
        else vendorId = v.id;
      }

      let companyId = defaultCompany?.id ?? '';
      const rawCompany = get('会社');
      if (rawCompany) {
        const c = findOne(masters.companies, rawCompany, (x) => [x.id, x.code, x.name]);
        if (c === null || c === 'many') errors.push(`会社「${rawCompany}」が見つかりません`);
        else companyId = c.id;
      }

      const amount = parseAmount(get('金額').replace(/^[¥￥]/, ''));
      if (amount === null) errors.push('金額は1円以上の整数で書いてください');

      if (errors.length > 0 || !postingDate || !dept || dept === 'many' || !account || account === 'many' || amount === null) {
        return { line, status: 'error', message: errors.join(' / '), input: null };
      }

      const input: DirectEntryInput = {
        companyId,
        departmentId: dept.id,
        accountId: account.id,
        subAccountId,
        hierarchyNodeId: null,
        postingDate,
        amount,
        counterparty: vendorId ? (masters.vendors.find((v) => v.id === vendorId)?.name ?? '') : '',
        description: get('摘要'),
        vendorId,
      };
      const key = duplicateKeyOf({ posting_date: postingDate, department_id: dept.id, account_id: account.id, sub_account_id: subAccountId, company_id: companyId, amount });
      const vendorsThere = existingVendors.get(key) ?? [];
      if (vendorsThere.some((v) => v === null || v === vendorId)) {
        return { line, status: 'duplicate', message: '同じ計上日・事業部・科目・会社・金額の取引が登録済み(取引先が同じか、登録済みの方に取引先が無い)', input };
      }
      // 同じ内容の行が表の中に2回以上あるときは、登録はするが目立たせる(同じ日に同じ金額の納品もあり得るため止めない)
      const first = seenInFile.get(key);
      if (first === undefined) seenInFile.set(key, line);
      const kind = account.pl_line === 'revenue' ? '売上' : '原価';
      return { line, status: 'new', message: first === undefined ? kind : `${kind}(${first}行目と同じ内容)`, input };
    });
}

/** 取り込む行の計上月(重複の判定のために既存の取引を読む月) */
export function monthsOfTable(table: CsvTable): string[] {
  const i = table.headers.findIndex((h) => h.normalize('NFKC').trim() === '計上日');
  if (i < 0) return [];
  const months = new Set<string>();
  for (const r of table.rows) {
    const d = parseDate(r[i] ?? '');
    if (d) months.add(d.slice(0, 7));
  }
  return [...months].sort();
}
